import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import axios from "axios";
import { getFullApiUrl } from "../../config";
import { formatDateSlash, getToken } from "../../utils";
import { ExpiredPage } from "../ExpiredPage";
import { LoadingPage } from "../LoadingPage";

/** 知识星球会员记录管理：创建购买记录（昵称/编号/开始时间/到期时间）、查看绑定情况、续期、删除。 */
export default function ZsxqManagementPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [message, setMessage] = useState(null); // {severity, text}
  const [processing, setProcessing] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", number: "", startAt: "", endAt: "" });

  const [renewTarget, setRenewTarget] = useState(null); // member 或 null
  const [renewStartAt, setRenewStartAt] = useState("");
  const [renewEndAt, setRenewEndAt] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [importFile, setImportFile] = useState(null); // File 或 null
  const [importResult, setImportResult] = useState(null); // 导入摘要或 null
  const importInputRef = useRef(null);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${getToken()}` },
  });

  const fetchMembers = useCallback(async () => {
    try {
      const response = await axios.get(
        getFullApiUrl("/api/v1/admin/zsxq-members"),
        authHeaders()
      );
      setMembers(response.data.members || []);
    } catch (err) {
      if (err.response?.data?.error === "token has expired") {
        setExpired(true);
      } else {
        console.error("Failed to fetch zsxq members:", err);
        setMessage({ severity: "error", text: "加载知识星球会员列表失败" });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleCreate = async () => {
    if (!form.name || !/^\d+$/.test(form.number) || !form.startAt || !form.endAt) {
      setMessage({
        severity: "error",
        text: "请完整填写昵称、数字编号、开始日期和到期日期",
      });
      return;
    }
    if (form.startAt >= form.endAt) {
      setMessage({ severity: "error", text: "到期日期必须晚于开始日期" });
      return;
    }
    setProcessing(true);
    try {
      await axios.post(
        getFullApiUrl("/api/v1/admin/zsxq-members"),
        {
          zsxq_name: form.name,
          zsxq_number: Number(form.number),
          membership_start_at: form.startAt,
          membership_end_at: form.endAt,
        },
        authHeaders()
      );
      setCreateOpen(false);
      setForm({ name: "", number: "", startAt: "", endAt: "" });
      setMessage({ severity: "success", text: "已创建知识星球会员记录" });
      await fetchMembers();
    } catch (err) {
      if (err.response?.status === 409) {
        setMessage({ severity: "error", text: "该昵称与编号的记录已存在" });
      } else {
        setMessage({
          severity: "error",
          text: err.response?.data?.error || "创建失败，请稍后重试",
        });
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleRenew = async () => {
    if (!renewTarget || !renewEndAt) return;
    if (renewStartAt && renewStartAt >= renewEndAt) {
      setMessage({ severity: "error", text: "到期日期必须晚于开始日期" });
      return;
    }
    setProcessing(true);
    try {
      const payload = { membership_end_at: renewEndAt };
      if (renewStartAt) {
        payload.membership_start_at = renewStartAt;
      }
      await axios.put(
        getFullApiUrl(`/api/v1/admin/zsxq-members/${renewTarget.id}`),
        payload,
        authHeaders()
      );
      setRenewTarget(null);
      setMessage({ severity: "success", text: "已更新会员有效期" });
      await fetchMembers();
    } catch (err) {
      setMessage({
        severity: "error",
        text: err.response?.data?.error || "更新失败，请稍后重试",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setProcessing(true);
    try {
      const data = new FormData();
      data.append("file", importFile);
      const response = await axios.post(
        getFullApiUrl("/api/v1/admin/zsxq-members/import"),
        data,
        authHeaders()
      );
      setImportResult(response.data.summary || {});
      await fetchMembers();
    } catch (err) {
      const status = err.response?.status;
      let text;
      if (err.response?.data?.error) {
        text = `导入失败：${err.response.data.error}`;
      } else if (status === 404) {
        text =
          "导入失败：服务器上不存在导入接口（HTTP 404），后端可能尚未部署包含该接口的版本";
      } else if (status) {
        text = `导入失败：服务器返回 HTTP ${status}${
          err.response.statusText ? ` ${err.response.statusText}` : ""
        }`;
      } else {
        text = `导入失败：${err.message || "网络错误，请检查网络后重试"}`;
      }
      setMessage({ severity: "error", text });
    } finally {
      setProcessing(false);
      setImportFile(null);
      if (importInputRef.current) {
        importInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setProcessing(true);
    try {
      await axios.delete(
        getFullApiUrl(`/api/v1/admin/zsxq-members/${deleteTarget.id}`),
        authHeaders()
      );
      setDeleteTarget(null);
      setMessage({ severity: "success", text: "已删除该记录及其绑定关系" });
      await fetchMembers();
    } catch (err) {
      setMessage({
        severity: "error",
        text: err.response?.data?.error || "删除失败，请稍后重试",
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (expired) {
    return <ExpiredPage />;
  }

  return (
    <Container component="main" maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1">
            知识星球会员管理
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            在这里录入知识星球的购买记录（昵称 + 成员编号 + 开始时间 +
            到期时间）。用户绑定后，星球会员期叠加计入总有效期，与已有付费时段重叠的部分自动顺延延长。
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton onClick={fetchMembers} aria-label="刷新">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => importInputRef.current?.click()}
          >
            导入表格
          </Button>
          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setImportFile(file);
              }
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            新增记录
          </Button>
        </Box>
      </Box>

      {message ? (
        <Alert
          severity={message.severity}
          sx={{ mb: 2 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      ) : null}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>会员昵称</TableCell>
              <TableCell>成员编号</TableCell>
              <TableCell>星球开始时间</TableCell>
              <TableCell>星球到期时间</TableCell>
              <TableCell>绑定账号</TableCell>
              <TableCell>绑定时间</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">
                    暂无记录，点击右上角「新增记录」创建。
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              members.map((m) => {
                const active = new Date(m.membership_end_at) > new Date();
                return (
                  <TableRow key={`${m.id}-${m.bound_email || "unbound"}`}>
                    <TableCell>{m.zsxq_name}</TableCell>
                    <TableCell>{m.zsxq_number}</TableCell>
                    <TableCell>
                      {m.membership_start_at
                        ? formatDateSlash(m.membership_start_at)
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {formatDateSlash(m.membership_end_at)}{" "}
                      <Chip
                        size="small"
                        label={active ? "有效" : "已过期"}
                        color={active ? "success" : "default"}
                        sx={{ ml: 1 }}
                      />
                    </TableCell>
                    <TableCell>
                      {m.bound_email || (
                        <Typography component="span" color="text.secondary">
                          未绑定
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {m.bound_at ? formatDateSlash(m.bound_at) : "—"}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        aria-label="修改会员有效期"
                        onClick={() => {
                          setRenewTarget(m);
                          setRenewStartAt(
                            m.membership_start_at
                              ? new Date(m.membership_start_at)
                                  .toISOString()
                                  .slice(0, 10)
                              : ""
                          );
                          setRenewEndAt(
                            new Date(m.membership_end_at)
                              .toISOString()
                              .slice(0, 10)
                          );
                        }}
                      >
                        <EditCalendarIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="删除"
                        onClick={() => setDeleteTarget(m)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 新增记录 */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>新增知识星球会员记录</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            fullWidth
            label="会员昵称"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoFocus
          />
          <TextField
            margin="normal"
            fullWidth
            label="成员编号"
            value={form.number}
            onChange={(e) =>
              setForm({ ...form, number: e.target.value.replace(/\D/g, "") })
            }
          />
          <TextField
            margin="normal"
            fullWidth
            label="星球会员开始日期"
            type="date"
            value={form.startAt}
            onChange={(e) => setForm({ ...form, startAt: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="normal"
            fullWidth
            label="星球会员到期日期"
            type="date"
            value={form.endAt}
            onChange={(e) => setForm({ ...form, endAt: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={processing}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={16} /> : null}
          >
            创建
          </Button>
        </DialogActions>
      </Dialog>

      {/* 修改会员有效期 */}
      <Dialog open={!!renewTarget} onClose={() => setRenewTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>修改会员有效期</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {renewTarget?.zsxq_name}（编号 {renewTarget?.zsxq_number}）
          </Typography>
          <TextField
            margin="normal"
            fullWidth
            label="星球会员开始日期"
            type="date"
            value={renewStartAt}
            onChange={(e) => setRenewStartAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="normal"
            fullWidth
            label="新的到期日期"
            type="date"
            value={renewEndAt}
            onChange={(e) => setRenewEndAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenewTarget(null)} disabled={processing}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleRenew}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={16} /> : null}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 导入确认 */}
      <Dialog
        open={!!importFile}
        onClose={() => {
          if (!processing) {
            setImportFile(null);
            if (importInputRef.current) {
              importInputRef.current.value = "";
            }
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>导入知识星球成员报表？</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            文件：{importFile?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            将按成员编号增量更新：新增成员、延长到期时间、回填开始时间、把已退出成员标记过期；不会删除任何记录。
            如果表格格式与预期不符（缺少关键列、日期或退出状态格式变化等），将整体报错并且不做任何修改。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setImportFile(null);
              if (importInputRef.current) {
                importInputRef.current.value = "";
              }
            }}
            disabled={processing}
          >
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={16} /> : null}
          >
            {processing ? "导入中…" : "导入"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 导入结果 */}
      <Dialog
        open={!!importResult}
        onClose={() => setImportResult(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>导入完成</DialogTitle>
        <DialogContent>
          {[
            ["表格数据行数", importResult?.total_rows],
            ["新增会员", importResult?.created],
            ["续费延期", importResult?.renewed],
            ["标记退出（过期）", importResult?.expired],
            ["更新昵称", importResult?.renamed],
            ["回填开始时间", importResult?.start_backfilled],
            ["无变化", importResult?.unchanged],
          ].map(([label, value]) => (
            <Box
              key={label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 0.5,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {value ?? 0}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setImportResult(null)}>
            知道了
          </Button>
        </DialogActions>
      </Dialog>

      {/* 删除确认 */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>确认删除？</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            将删除「{deleteTarget?.zsxq_name}（编号 {deleteTarget?.zsxq_number}
            ）」的会员记录
            {deleteTarget?.bound_email
              ? `，并解除其与 ${deleteTarget.bound_email} 的绑定`
              : ""}
            。此操作不可撤销。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={processing}>
            取消
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={16} /> : null}
          >
            删除
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
